/**
 * ProgressBar
 * 
 * 职责：进度条组件，负责展示播放进度和允许用户拖动跳转
 * - 半受控模式：播放时 value 来自 video，拖动时使用本地 draggingValue
 * - 拖动时只更新视觉，onMouseUp/onTouchEnd 时才 seek
 */

import React from 'react';
import type { PlayerActions } from '../../hooks/usePlayerEngine';

interface ProgressBarProps {
  currentTime: number; // 用于进度条 value（精确值）
  displayTime: number; // 【建议6】用于时间显示文本（节流后的值，≤10fps）
  videoDuration: number;
  disabled: boolean;
  formatTime: (seconds: number) => string;
  controlAreaRef: React.RefObject<HTMLDivElement | null>;
  isDraggingRef: React.MutableRefObject<boolean>;
  wasPlayingBeforeDragRef: React.MutableRefObject<boolean>;
  draggingValueRef: React.MutableRefObject<number | null>;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  safeSeek: (time: number, onSeeked?: () => void) => void;
  playerActions: PlayerActions; // 【P1】播放器 actions，用于统一操作 video
  setCurrentTime: (time: number | ((prev: number) => number)) => void;
  setDisplayTime: (time: number) => void; // 【建议6】用于拖动时立即更新显示时间
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  currentTime,
  displayTime,
  videoDuration,
  disabled,
  formatTime,
  controlAreaRef,
  isDraggingRef,
  wasPlayingBeforeDragRef,
  draggingValueRef,
  videoRef,
  safeSeek,
  playerActions,
  setCurrentTime,
  setDisplayTime,
}) => {
  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    const newTime = parseFloat((e.currentTarget.value));
    // 如果是第一次触发 onInput（拖动开始），记录播放状态
    if (!isDraggingRef.current && videoRef.current) {
      isDraggingRef.current = true;
      wasPlayingBeforeDragRef.current = !videoRef.current.paused;
    }
    // 更新本地拖动值，不更新 React state
    draggingValueRef.current = newTime;
    // 直接更新进度条视觉（CSS 变量），不触发 React re-render
    if (controlAreaRef.current && videoDuration > 0) {
      const percent = (newTime / videoDuration) * 100;
      controlAreaRef.current.style.setProperty('--progress', `${percent}%`);
    }
    // 更新时间显示文本（拖动时也需要显示，立即更新不节流）
    setCurrentTime(newTime);
    // 【建议6】拖动时立即更新显示时间，不节流（保证拖动时的实时反馈）
    setDisplayTime(newTime);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (videoRef.current && isDraggingRef.current) {
      // 拖动结束：使用安全的 seek 函数，确保 HLS 视频可靠跳转
      const shouldResumePlayback = wasPlayingBeforeDragRef.current;
      // 【P1】使用 player actions 进行 seek，支持自动播放选项
      playerActions.seek(newTime, {
        autoPlayAfterSeek: shouldResumePlayback,
        onSeeked: () => {
          setCurrentTime(newTime);
          draggingValueRef.current = null;
        },
      });
      // 重置拖动状态
      isDraggingRef.current = false;
      wasPlayingBeforeDragRef.current = false;
    } else {
      // 非拖动情况（例如点击跳转）：使用安全的 seek 函数
      safeSeek(newTime, () => {
        setCurrentTime(newTime);
      });
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDraggingRef.current && draggingValueRef.current !== null && videoRef.current) {
      const newTime = draggingValueRef.current;
      const shouldResumePlayback = wasPlayingBeforeDragRef.current;
      // 在 onMouseUp 时执行 seek（确保 Safari 等浏览器也能正常工作）
      // 【P1】使用 player actions 进行 seek，支持自动播放选项
      playerActions.seek(newTime, {
        autoPlayAfterSeek: shouldResumePlayback,
        onSeeked: () => {
          setCurrentTime(newTime);
          draggingValueRef.current = null;
        },
      });
      isDraggingRef.current = false;
      wasPlayingBeforeDragRef.current = false;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (isDraggingRef.current && draggingValueRef.current !== null && videoRef.current) {
      const newTime = draggingValueRef.current;
      const shouldResumePlayback = wasPlayingBeforeDragRef.current;
      // 在 onTouchEnd 时执行 seek（确保移动端也能正常工作）
      // 【P1】使用 player actions 进行 seek，支持自动播放选项
      playerActions.seek(newTime, {
        autoPlayAfterSeek: shouldResumePlayback,
        onSeeked: () => {
          setCurrentTime(newTime);
          draggingValueRef.current = null;
        },
      });
      isDraggingRef.current = false;
      wasPlayingBeforeDragRef.current = false;
    }
  };

  return (
    <div className="progress-bar-wrapper">
      {/* 【建议6】使用节流后的 displayTime 显示文本，避免高频更新 */}
      <span className="time-display time-current">
        {formatTime(displayTime)}
      </span>
      {/* 半受控 range：播放时 value 来自 video，拖动时使用本地 draggingValue */}
      <input
        type="range"
        min="0"
        max={videoDuration}
        value={isDraggingRef.current && draggingValueRef.current !== null 
          ? draggingValueRef.current 
          : currentTime}
        disabled={disabled}
        onInput={handleInput}
        onChange={handleChange}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        className="custom-progress-bar"
      />
      <span className="time-display time-total">
        {formatTime(videoDuration)}
      </span>
    </div>
  );
};

